/**
 * UserForm Component
 *
 * Form for adding/editing users.
 * Role selection is restricted based on current user's role:
 * - super_admin: Can create any role
 * - admin: Can only create viewers
 */
import { useState, useCallback, useEffect } from 'react';
import { Modal, FormField } from '../ui';
import type { Client, User, UserFormData, UserRole } from '../../types';

interface UserFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserFormData) => Promise<void>;
  user?: User | null;
  clients: Client[];
  selectedClientId?: string | null;
  isLoading?: boolean;
  allowedRoles?: UserRole[];  // Roles the current user can assign
  showClientSelect?: boolean; // Super admin can select client, admin cannot
}

const roleLabels: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  viewer: 'Viewer',
};

const initialFormData: UserFormData = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  clientId: null,
  role: 'viewer',
};

export const UserForm = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  clients,
  selectedClientId,
  isLoading = false,
  allowedRoles = ['viewer'],
  showClientSelect = true,
}: UserFormProps) => {
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!user;

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone || '',
          clientId: user.clientId,
          role: user.role,
        });
      } else {
        setFormData({
          ...initialFormData,
          clientId: selectedClientId || null,
          role: allowedRoles[0] || 'viewer',
        });
      }
      setErrors({});
    }
  }, [isOpen, user, selectedClientId, allowedRoles]);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Client is required unless creating a super_admin
    if (showClientSelect && !formData.clientId && formData.role !== 'super_admin') {
      newErrors.clientId = 'Client is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, showClientSelect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    await onSubmit(formData);
  };

  const handleChange = (field: keyof UserFormData, value: string | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit User' : 'Add User'}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <FormField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          error={errors.email}
          required
          placeholder="user@example.com"
          disabled={isLoading || isEditMode}
          helperText={isEditMode ? 'Email cannot be changed after creation' : undefined}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={errors.firstName}
            required
            placeholder="John"
            disabled={isLoading}
          />

          <FormField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={errors.lastName}
            required
            placeholder="Doe"
            disabled={isLoading}
          />
        </div>

        <FormField
          label="Phone"
          type="tel"
          value={formData.phone || ''}
          onChange={(e) => handleChange('phone', e.target.value)}
          placeholder="+1 234 567 8900"
          disabled={isLoading}
        />

        {/* Client select - shown to super_admin, hidden for client admin */}
        {showClientSelect && (
          <FormField
            label="Client"
            error={errors.clientId}
            required={formData.role !== 'super_admin'}
          >
            <select
              value={formData.clientId || ''}
              onChange={(e) => handleChange('clientId', e.target.value || null)}
              disabled={isLoading}
              className={`
                w-full px-3 py-2 text-sm
                border rounded-lg
                focus:outline-none focus:ring-2 focus:ring-offset-0
                ${errors.clientId
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-metrica-green focus:border-metrica-green'
                }
                disabled:bg-gray-100 disabled:cursor-not-allowed
              `}
            >
              <option value="">
                {formData.role === 'super_admin' ? 'No client (Super Admin)' : 'Select a client'}
              </option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </FormField>
        )}

        {/* Role select */}
        <FormField label="Role" error={errors.role} required>
          <select
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value as UserRole)}
            disabled={isLoading || isEditMode}
            className={`
              w-full px-3 py-2 text-sm
              border rounded-lg
              focus:outline-none focus:ring-2 focus:ring-offset-0
              ${errors.role
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-metrica-green focus:border-metrica-green'
              }
              disabled:bg-gray-100 disabled:cursor-not-allowed
            `}
          >
            {allowedRoles.map((role) => (
              <option key={role} value={role}>
                {roleLabels[role]}
              </option>
            ))}
          </select>
          {isEditMode && (
            <p className="mt-1 text-xs text-gray-500">Role cannot be changed after creation</p>
          )}
        </FormField>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green disabled:opacity-50"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </span>
            ) : isEditMode ? (
              'Update User'
            ) : (
              'Create User'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UserForm;
